// Product + hub content data for Ninja Athletic Club.
// Waitlist/contact/subscribe are handled by the FastAPI backend (src/lib/api.js).

import { blogArticles } from "./blogArticles";
import { drop002Products } from "./drop002";

const blogCards = blogArticles.map((a) => ({
  slug: a.slug,
  href: `/mindset/${a.slug}`,
  title: a.title,
  excerpt: a.excerpt,
  image: a.heroThumb,
  alt: a.heroAlt,
  readTime: a.readTime,
  available: true,
}));

const drop001 = [
  {
    slug: "discipline-is-the-weapon",
    title: "Discipline Is The Weapon",
    kanji: "刀",
    metaTitle: "Discipline Is The Weapon Canvas Print | Ninja Athletic Club",
    metaDescription:
      "A lone ronin, moonlight, and a decision. Museum-grade canvas in a matte black float frame. Warrior mindset wall art from Ninja Athletic Club. From $79.",
    short: "Moonlit ronin. Rain. No excuses.",
    description:
      "A lone ronin on a rain soaked rooftop. Moonlight, rain, and a decision. This is the piece for the wall you see before every 5 AM session. Museum-grade canvas, matte black float frame, the club seal in the corner.",
    art: "/images/art-discipline-1200.webp",
    artThumb: "/images/art-discipline-800.webp",
    room: "/images/room-discipline-1200.webp",
    roomThumb: "/images/room-discipline-800.webp",
    altArt: "Masked ninja in black holding a katana in a dark industrial hall, Discipline Is The Weapon canvas print",
    altRoom: "Discipline Is The Weapon framed canvas print in a matte black float frame on a dark gallery wall",
    sizes: [
      { label: "12 x 18", price: 79 },
      { label: "18 x 27", price: 119 },
      { label: "24 x 36", price: 159 },
    ],
  },
  {
    slug: "fall-seven-rise-eight",
    title: "Fall Seven. Rise Eight.",
    kanji: "忍",
    metaTitle: "Fall Seven Rise Eight Canvas Print | Ninja Athletic Club",
    metaDescription:
      "Nana korobi ya oki. The proverb of rising, framed. Museum-grade canvas in a matte black float frame. Motivational wall art from Ninja Athletic Club. From $79.",
    short: "The old proverb. The only rule that matters.",
    description:
      "Nana korobi ya oki. The samurai rises through embers because the count starts at rising. For the comeback season, the rehab block, the year that tested you.",
    art: "/images/art-fall-1200.webp",
    artThumb: "/images/art-fall-800.webp",
    room: "/images/room-fall-1200.webp",
    roomThumb: "/images/room-fall-800.webp",
    altArt: "Armored warrior seated beside embers and firelight at night, Fall Seven Rise Eight canvas print",
    altRoom: "Fall Seven Rise Eight framed canvas print in a matte black float frame on a dark gallery wall",
    sizes: [
      { label: "12 x 18", price: 79 },
      { label: "18 x 27", price: 119 },
      { label: "24 x 36", price: 159 },
    ],
  },
  {
    slug: "master-yourself-first",
    title: "Master Yourself First",
    kanji: "道",
    metaTitle: "Master Yourself First Canvas Print | Ninja Athletic Club",
    metaDescription:
      "Aikido at first light. Calm power over chaos, framed. Museum-grade canvas in a matte black float frame. Warrior wall art from Ninja Athletic Club. From $79.",
    short: "Aikido at dawn. Calm power over chaos.",
    description:
      "Aikido at first light. Calm power over chaos. The reminder that the only opponent who shows up every single day is you.",
    art: "/images/art-master-1200.webp",
    artThumb: "/images/art-master-800.webp",
    room: "/images/room-master-1200.webp",
    roomThumb: "/images/room-master-800.webp",
    altArt: "Warrior silhouette holding a sword at first light on a misty coast, Master Yourself First canvas print",
    altRoom: "Master Yourself First framed canvas print in a matte black float frame on a dark gallery wall",
    sizes: [
      { label: "12 x 18", price: 79 },
      { label: "18 x 27", price: 119 },
      { label: "24 x 36", price: 159 },
    ],
  },
].map((p) => ({ ...p, drop: "001" }));

export const products = [...drop001, ...drop002Products];

export const productDetails = [
  "Museum-grade matte canvas",
  "Matte black float frame",
  "The club seal in the corner",
  "Limited numbered run.",
];

export const hubs = {
  mindset: {
    path: "/mindset",
    name: "Mindset",
    seal: "心",
    kicker: "The Mind",
    metaTitle: "Warrior Mindset & Bushido for Athletes | Ninja Athletic Club",
    metaDescription:
      "Bushido, aikido, and the discipline codes of history's greatest warriors, translated for modern hybrid athletes. The mindset hub of Ninja Athletic Club.",
    h1: ["The blade is forged", "in the mind."],
    sub: "Bushido, aikido, and the discipline codes of history's greatest warriors, translated for the modern athlete.",
    answer: {
      q: "What is a warrior mindset?",
      a: "A warrior mindset is the practice of meeting difficulty on purpose. It is built through daily discipline, composure under stress, and a code you keep when nobody is watching. Ninja Athletic Club translates the old codes, bushido, aikido, and the writings of Musashi, into tools for modern training.",
    },
    tenets: [
      { title: "Discipline", text: "The weapon you sharpen daily. Motivation is weather. Discipline is climate." },
      { title: "Composure", text: "Calm is a trained response. Breathe slow when everything says panic." },
      { title: "Detachment", text: "Release what you cannot control. Grip only the next rep." },
      { title: "Resilience", text: "Fall seven. Rise eight. The count starts at rising." },
      { title: "Honor", text: "Hold the standard in the dark. The empty gym is the real judge." },
    ],
    articles: [
      {
        slug: "kaizen-for-athletes",
        href: "/mindset/kaizen-for-athletes",
        title: "Kaizen: Win by One Percent, Forever",
        excerpt: "Change for the better, one hammer strike at a time. The forge philosophy that ends plateaus.",
        image: "/images/kaizen-800.webp",
        alt: "Blacksmith hammering glowing steel in a dark forge, artwork for the kaizen article",
        readTime: "7 min read",
        available: true,
      },
      {
        slug: "ikigai-for-athletes",
        href: "/mindset/ikigai-for-athletes",
        title: "Ikigai: The Reason You Rise Before the Sun",
        excerpt: "A reason for being, small enough to survive a bad day. Purpose is the grip on the weapon.",
        image: "/images/ikigai-800.webp",
        alt: "Torii gate on a forest path with morning light rays, artwork for the ikigai article",
        readTime: "7 min read",
        available: true,
      },
      {
        slug: "musashi-dokkodo-for-athletes",
        href: "/mindset/musashi-dokkodo-for-athletes",
        title: "Musashi's Dokkodo, Translated for Athletes",
        excerpt: "Twenty one precepts written a week before the sword saint died. Every one of them still coaches.",
        image: "/images/art-discipline-800.webp",
        alt: "Masked ninja holding a katana in a dark hall, artwork for the Dokkodo article",
        readTime: "8 min read",
        available: true,
      },
      {
        slug: "aikido-principles-hybrid-athletes",
        href: "/mindset/aikido-principles-hybrid-athletes",
        title: "Aikido Principles for Hybrid Athletes",
        excerpt: "Blend instead of resist. Relax under load. Redirect discomfort. The soft art has hard lessons for race day.",
        image: "/images/art-master-800.webp",
        alt: "Warrior silhouette with a sword at first light, artwork for the aikido principles article",
        readTime: "6 min read",
        available: true,
      },
      {
        slug: "fall-seven-rise-eight-meaning",
        href: "/mindset/fall-seven-rise-eight-meaning",
        title: "Fall Seven Times, Rise Eight: What It Really Means",
        excerpt: "The proverb, its origin, and the math of the count. Applied to DNFs, injuries, and plateaus.",
        image: "/images/art-fall-800.webp",
        alt: "Armored warrior seated beside embers at night, artwork for the fall seven rise eight article",
        readTime: "6 min read",
        available: true,
      },
      ...blogCards,
    ],
    faq: [
      {
        q: "What is bushido?",
        a: "Bushido is the historical code of the samurai, usually summarized through virtues such as rectitude, courage, benevolence, respect, honesty, honor, and loyalty. At Ninja Athletic Club we treat it as a daily practice for athletes, not a costume.",
      },
      {
        q: "What is the Dokkodo?",
        a: "The Dokkodo, meaning the path of aloneness, is a short list of 21 precepts written by swordsman Miyamoto Musashi in 1645, days before his death. It condenses a lifetime of discipline into rules for self mastery.",
      },
      {
        q: "What does fall seven times rise eight mean?",
        a: "Nana korobi ya oki is a Japanese proverb meaning that getting up matters more than falling down. The extra rise reflects the idea that you begin life down and rising is the default, so the count of recoveries always outnumbers the falls.",
      },
      {
        q: "Is aikido philosophy useful for athletes?",
        a: "Yes. Aikido emphasizes blending with force instead of resisting it, staying relaxed under pressure, and redirecting energy. Those same skills govern pacing, discomfort management, and composure in endurance and hybrid racing.",
      },
    ],
    quote: { text: "Do nothing which is of no use.", attr: "Miyamoto Musashi" },
    lab: "For the science of focus, stress, and sleep, the research library lives at NinjAthlete.",
  },
  train: {
    path: "/train",
    name: "Train",
    seal: "体",
    kicker: "The Body",
    metaTitle: "Hybrid Athlete Training Culture | Ninja Athletic Club",
    metaDescription:
      "What is a hybrid athlete? HYROX culture, pacing, strength, and the mentality of race day. The training hub of Ninja Athletic Club. Dangerous at everything.",
    h1: ["Dangerous at", "everything."],
    sub: "Hybrid training culture. Run fast, lift heavy, repeat forever.",
    answer: {
      q: "What is a hybrid athlete?",
      a: "A hybrid athlete trains strength and endurance at the same time instead of choosing one. The goal is to be dangerous at everything: strong enough to move heavy loads, fit enough to run for an hour, durable enough to do both tomorrow. HYROX and DEKA are the proving grounds of the sport.",
    },
    tenets: [
      { title: "Consistency", text: "Show up on the days that do not count. Those are the ones that do." },
      { title: "Pacing", text: "Races are won at threshold, not at the start line. Learn to hold back early." },
      { title: "Strength", text: "Sleds, carries, lunges. Get strong enough that heavy feels neutral." },
      { title: "Engine", text: "Zone 2 builds the base. Intervals sharpen the blade. Both, forever." },
      { title: "Race Day", text: "Nothing new on race day. Execute the plan you rehearsed a hundred times." },
    ],
    articles: [
      {
        slug: "hyrox-stations-ranked",
        href: "/train",
        title: "The HYROX Stations, Ranked by How They Break People",
        excerpt: "Eight stations. Eight different ways to find out who you are. A field guide to the damage.",
        image: "/images/train-stations-800.webp",
        alt: "Black and white low angle silhouette of a warrior with a conical hat and katana",
        kanji: "体",
        readTime: "Dispatch incoming",
        available: false,
      },
      {
        slug: "zone-2-guide",
        href: "/train",
        title: "Zone 2 Is Boring. Do It Anyway.",
        excerpt: "The least cinematic training you will ever do, and the reason you finish strong while others fade.",
        image: "/images/train-zone2-800.webp",
        alt: "Runner silhouette on a wet road at dawn with sunrise light reflecting",
        kanji: "息",
        readTime: "Dispatch incoming",
        available: false,
      },
      {
        slug: "wall-balls-hyrox",
        href: "/train",
        title: "The Wall Ball Standard: Surviving the Last Station",
        excerpt: "One hundred reps between you and the finish line. How the last station rewrites the whole race.",
        image: "/images/train-wallballs-800.webp",
        alt: "Warrior silhouette practicing sword cuts against a dramatic sunset sky",
        kanji: "刀",
        readTime: "Dispatch incoming",
        available: false,
      },
    ],
    faq: [
      {
        q: "What is HYROX?",
        a: "HYROX is an indoor fitness race that combines 8 rounds of 1 km running with 8 functional workout stations: SkiErg, sled push, sled pull, burpee broad jumps, rowing, farmers carry, sandbag lunges, and wall balls. Every race in the world uses the same format, which makes times comparable across events.",
      },
      {
        q: "How long is a HYROX race?",
        a: "A HYROX race covers 8 km of running plus the 8 stations. Most everyday athletes finish somewhere between 60 and 100 minutes, while the fastest athletes in the world finish in under an hour.",
      },
      {
        q: "What is a good HYROX time?",
        a: "It depends on division and age group. Finishing your first open race under 90 minutes is a solid benchmark, under 75 minutes is competitive, and elite fields race for times under an hour. The honest answer: a good time is faster than your last one.",
      },
      {
        q: "Hybrid athlete vs CrossFit, what is the difference?",
        a: "CrossFit is built around constantly varied, high intensity workouts with a large skill component. Hybrid training focuses on two measurable engines, strength and endurance, usually organized around running and lifting. HYROX and DEKA are the standardized race formats where hybrid athletes compete.",
      },
    ],
    quote: { text: "The more you sweat in training, the less you bleed in battle.", attr: "Warrior proverb" },
    lab: "Programs, protocols, and the HYROX gym directory live at NinjAthlete.",
    road: "Chasing road miles and run culture? The running arm of the family lives at NinjaRunClub.",
  },
  recover: {
    path: "/recover",
    name: "Recover",
    seal: "息",
    kicker: "The Breath",
    metaTitle: "Recovery Rituals for Hybrid Athletes | Ninja Athletic Club",
    metaDescription:
      "Heat, cold, breath, and sleep as rituals, not rewards. Why recovery matters for hybrid athletes. The recovery hub of Ninja Athletic Club.",
    h1: ["The warrior rests", "with intent."],
    sub: "Heat, cold, breath, and sleep as rituals, not rewards.",
    answer: {
      q: "Why does recovery matter for hybrid athletes?",
      a: "Training creates the stimulus. Recovery creates the adaptation. Hybrid athletes carry double the training stress of single-sport athletes, so sleep, heat and cold exposure, breathwork, and deload weeks are not luxuries. They are half of the program.",
    },
    tenets: [
      { title: "Sleep", text: "Guard it like a training session. It is where the adaptation actually happens." },
      { title: "Heat", text: "Sauna as ritual. Sit in the discomfort until it becomes stillness." },
      { title: "Cold", text: "The composure rep. Enter calm, breathe slow, leave sharper than you came." },
      { title: "Breath", text: "The nervous system obeys the exhale. Slow it down and the body follows." },
      { title: "Deload", text: "Back off on purpose so you never get backed off by force." },
    ],
    articles: [
      {
        slug: "sauna-ritual",
        href: "/recover",
        title: "Sauna as Ritual: The Warrior's Heat",
        excerpt: "Heat as a practice, not a perk. What the sauna teaches about sitting with discomfort.",
        image: "/images/recover-sauna-800.webp",
        alt: "Warrior silhouette holding a sword against a warm sunset sky",
        kanji: "息",
        readTime: "Dispatch incoming",
        available: false,
      },
      {
        slug: "cold-exposure-mindset",
        href: "/recover",
        title: "Cold Water and the Composure Rep",
        excerpt: "The plunge is not about the cold. It is about who is in charge of your breathing.",
        image: "/images/recover-cold-800.webp",
        alt: "Cold fog rolling over a dark evergreen forest",
        kanji: "心",
        readTime: "Dispatch incoming",
        available: false,
      },
      {
        slug: "box-breathing",
        href: "/recover",
        title: "Box Breathing Before the Start Line",
        excerpt: "Four counts in, four hold, four out, four hold. The oldest pre-race ritual there is.",
        image: "/images/recover-breath-800.webp",
        alt: "Seated meditation silhouette facing the rising sun over distant mountains",
        kanji: "問",
        readTime: "Dispatch incoming",
        available: false,
      },
    ],
    faq: [
      {
        q: "How much sleep do athletes need?",
        a: "Most adults need 7 to 9 hours. Athletes in heavy training blocks often benefit from the top of that range or more, because sleep is when tissue repair and adaptation happen. For study citations and dosage detail, see the recovery library at NinjAthlete.",
      },
      {
        q: "Sauna before or after training?",
        a: "After. Heat after training fits recovery and relaxation, while sauna before a hard session adds heat stress and dehydration exactly when you need output. Treat it as the closing ritual of the day, not the warm up.",
      },
      {
        q: "Do ice baths blunt muscle growth?",
        a: "Some research suggests cold water immersion immediately after strength training can reduce hypertrophy signaling, so many lifters separate cold exposure from lifting by several hours. For the full study breakdowns, go to the lab at NinjAthlete.",
      },
      {
        q: "What is box breathing?",
        a: "Box breathing is a four part breathing pattern: inhale for four counts, hold for four, exhale for four, hold for four. It is used by military units and athletes to settle the nervous system before and after stress.",
      },
    ],
    quote: { text: "After victory, tighten your helmet cords.", attr: "Samurai proverb" },
    lab: "For dosage, temperature, and study citations, see the recovery science library at NinjAthlete.",
  },
};

export const marqueeItems = [
  "Hybrid Sport",
  "Ninja Discipline",
  "HYROX",
  "Aikido",
  "Recovery",
  "Warrior Mindset",
];
